import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

// Polls for new website contact-form submissions and fires a browser
// notification + a short sound anywhere in the CRM. Mounted once inside the
// authenticated Layout. Sound needs a prior user gesture (browser autoplay
// policy) — we prime an AudioContext on the first click.
let audioCtx = null;

const primeAudio = () => {
  if (audioCtx) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  } catch { /* noop */ }
};

const playDing = () => {
  if (!audioCtx) return;
  try {
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    [660, 990].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = now + i * 0.14;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.36);
    });
  } catch { /* noop */ }
};

export const WebsiteFormNotifier = () => {
  const navigate = useNavigate();
  const lastId = useRef(undefined); // undefined = not yet baselined
  const unseen = useRef(0);
  const flashTimer = useRef(null);
  const originalTitle = useRef(typeof document !== 'undefined' ? document.title : '');

  const stopFlash = () => {
    if (flashTimer.current) { clearInterval(flashTimer.current); flashTimer.current = null; }
    unseen.current = 0;
    if (originalTitle.current) document.title = originalTitle.current;
  };

  const startFlash = () => {
    if (flashTimer.current) return;
    let on = false;
    flashTimer.current = setInterval(() => {
      on = !on;
      document.title = on
        ? `(${unseen.current}) 🔔 New website enquiry`
        : originalTitle.current;
    }, 1000);
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    const onGesture = () => primeAudio();
    window.addEventListener('click', onGesture, { once: true });
    const onVisible = () => { if (!document.hidden) stopFlash(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('click', onGesture);
      document.removeEventListener('visibilitychange', onVisible);
      stopFlash();
    };
  }, []);

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      try {
        const { data } = await api.get('/website-form/summary');
        const id = data.latest_id;
        if (lastId.current === undefined) {
          lastId.current = id || null; // baseline on first poll — don't alert
          return;
        }
        if (id && id !== lastId.current) {
          lastId.current = id;
          const preview = data.latest_preview || 'New website enquiry';
          if ('Notification' in window && Notification.permission === 'granted') {
            const n = new Notification('New website enquiry', {
              body: preview,
              tag: 'website-form',
              icon: '/favicon.ico',
            });
            n.onclick = () => { window.focus(); navigate('/website-leads'); n.close(); };
          }
          playDing();
          if (document.hidden) {
            unseen.current += 1;
            originalTitle.current = originalTitle.current || document.title;
            startFlash();
          }
        }
      } catch { /* noop */ }
    };

    poll();
    const t = setInterval(() => { if (!stopped) poll(); }, 15000);
    const onRefresh = () => poll();
    window.addEventListener('website-form-refresh', onRefresh);
    return () => { stopped = true; clearInterval(t); window.removeEventListener('website-form-refresh', onRefresh); };
  }, [navigate]);

  return null;
};

export default WebsiteFormNotifier;
